/**
 * Base-unit strings to and from human decimals ("1,000.50"), with BigInt on
 * the integer and fraction parts: no floating point. `decimals` is the
 * descriptor's; null (a token it does not name) gives no amount, never a guess.
 */

/**
 * Parse a human decimal amount ("1,000.50") into an integer base-unit string
 * ("1000500000"). Thousands separators (commas, spaces, underscores,
 * apostrophes) are stripped. Digits beyond `decimals` are truncated.
 * Returns null for empty/invalid/negative input or unknown decimals.
 */
export function toBaseUnits(input: string, decimals: number | null): string | null {
  if (decimals === null) return null;
  const cleaned = input.replace(/[,\s_']/g, "");
  if (cleaned === "" || cleaned === "." || !/^\d*\.?\d*$/.test(cleaned)) return null;

  const dot = cleaned.indexOf(".");
  const intPart = dot === -1 ? cleaned : cleaned.slice(0, dot);
  const fracPart = dot === -1 ? "" : cleaned.slice(dot + 1);
  // Truncate (not round) extra fractional digits, pad the rest with zeros.
  const frac = fracPart.slice(0, decimals).padEnd(decimals, "0");

  const scale = 10n ** BigInt(decimals);
  const base = BigInt(intPart === "" ? "0" : intPart) * scale + BigInt(frac === "" ? "0" : frac);
  return base.toString();
}

/**
 * Convert an integer base-unit string ("1000500000") back to a plain decimal
 * string ("1000.5"). Trailing fractional zeros are trimmed. Returns null for
 * invalid input or unknown decimals.
 */
export function fromBaseUnits(base: string, decimals: number | null): string | null {
  if (decimals === null) return null;
  const trimmed = base.trim();
  if (!/^\d+$/.test(trimmed)) return null;

  const value = BigInt(trimmed);
  const scale = 10n ** BigInt(decimals);
  const whole = value / scale;
  const frac = (value % scale).toString().padStart(decimals, "0").replace(/0+$/, "");
  return frac === "" ? whole.toString() : `${whole.toString()}.${frac}`;
}

const baseAmountFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Format an integer base-unit string for display: "499928920000" ->
 * "499,928.92" (always 2 decimals). Returns "—" for invalid input or unknown decimals.
 */
export function formatBaseUnits(base: string | null | undefined, decimals: number | null): string {
  if (base === null || base === undefined) return "—";
  const decimal = fromBaseUnits(String(base), decimals);
  if (decimal === null) return "—";
  return baseAmountFormatter.format(Number(decimal));
}

const groupFormatter = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });

/**
 * For an editable field: grouped ("99,500.5") but never rounded, since the user
 * quotes against what it writes back. "" for invalid input keeps the input controlled.
 */
export function formatBaseUnitsExact(
  base: string | null | undefined,
  decimals: number | null,
): string {
  if (base === null || base === undefined) return "";
  const decimal = fromBaseUnits(String(base), decimals);
  if (decimal === null) return "";
  const dot = decimal.indexOf(".");
  if (dot === -1) return groupFormatter.format(BigInt(decimal));
  return `${groupFormatter.format(BigInt(decimal.slice(0, dot)))}.${decimal.slice(dot + 1)}`;
}

/**
 * True when the input parses to a strictly positive base-unit amount at the
 * given precision (e.g. "0.001" is NOT positive for a 2-decimal token).
 */
export function isPositiveAmount(input: string, decimals: number | null): boolean {
  const base = toBaseUnits(input, decimals);
  return base !== null && base !== "0";
}
